
import { useState } from 'react';
import { Product } from '../page';
import ProductCard from './ProductCard';
import { CartItem } from './Cart';
import { useAuth } from '../../../contexts/AuthContext';

interface ProductListProps {
  products: Product[];
  onAddProduct: () => void;
  onEditProduct: (product: Product) => void;
  onViewProduct: (product: Product) => void;
  onDeleteProduct: (productId: string) => void;
  onUpdateVariantQuantity: (productId: string, size: string, color: string, quantity: number) => void;
  onImportStock: () => void;
  onViewHistory: () => void;
  searchTerm: string;
  onSearchChange: (term: string) => void;
  filterCategory: string;
  onFilterChange: (category: string) => void;
  categories: string[];
  getTotalQuantity: (product: Product) => number;
  onAddToCart: (item: Omit<CartItem, 'id'>) => void;
}

export default function ProductList({
  products,
  onAddProduct,
  onEditProduct,
  onViewProduct,
  onDeleteProduct,
  onUpdateVariantQuantity,
  onImportStock,
  onViewHistory,
  searchTerm,
  onSearchChange,
  filterCategory,
  onFilterChange,
  categories = [], // Add default empty array
  getTotalQuantity,
  onAddToCart
}: ProductListProps) {
  const { userProfile } = useAuth();
  
  // Check if user is partner
  const isPartner = userProfile?.role === 'partner';

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Filters */}
      <div className="bg-white p-4 sm:p-6 rounded-lg border border-gray-200">
        <div className="space-y-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4">
          <div className="sm:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Tìm kiếm</label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Tìm theo tên, mã sản phẩm..."
                className="w-full pl-10 pr-4 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Danh mục</label>
            <select
              value={filterCategory}
              onChange={(e) => onFilterChange(e.target.value)}
              className="w-full px-3 py-2 sm:py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-colors text-sm pr-8"
            >
              <option value="">Tất cả danh mục</option>
              {categories && Array.isArray(categories) && categories.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <button
            onClick={() => {
              onSearchChange('');
              onFilterChange('');
            }}
            className="w-full sm:w-auto border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
          >
            Xóa bộ lọc
          </button>
          <div className="text-sm text-gray-600 text-center sm:text-right">
            <span className="font-medium">{products?.length || 0}</span> sản phẩm
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {!products || products.length === 0 ? (
          <div className="text-center py-8 sm:py-12 px-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-box-3-line text-gray-400 text-xl sm:text-2xl"></i>
            </div>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Chưa có sản phẩm</h3>
            <p className="text-gray-600 mb-4 text-sm sm:text-base">
              {isPartner ? 'Hiện tại chưa có sản phẩm nào' : 'Thêm sản phẩm đầu tiên để bắt đầu'}
            </p>
            {!isPartner && (
              <button
                onClick={onAddProduct}
                className="bg-orange-600 text-white hover:bg-orange-700 px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
              >
                Thêm sản phẩm
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 p-4 sm:p-6">
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                onEdit={() => onEditProduct(product)}
                onView={() => onViewProduct(product)}
                onDelete={() => onDeleteProduct(product.id)}
                onAddToCart={onAddToCart}
                showActions={!isPartner} // Hide edit/delete actions for partners
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
